import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { AuthService } from "@/services";
import { useAuth } from "@/contexts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Logo } from "@/components/core/logo";
import { ArrowLeft } from "lucide-react";
import { formatPhoneInput } from "@/lib/utils";
import { showToast } from "@/hooks/useToast";

export const Route = createFileRoute("/auth/verify-phone")({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      telefone: (search.telefone as string) || "",
    };
  },
  component: RouteComponent,
});

const phoneSchema = z.object({
  telefone: z
    .string()
    .min(1, "Informe um número de telefone")
    .refine(
      (value) => value.replace(/[^0-9]+/g, "").length >= 10,
      "Informe um número com DDD"
    ),
});

function RouteComponent() {
  const navigate = useNavigate();
  const { saveAuth } = useAuth();
  const { telefone: telefoneFromQuery } = useSearch({ from: "/auth/verify-phone" });

  const [isLoading, setIsLoading] = useState(false);
  const [phoneStep, setPhoneStep] = useState<"form" | "code">("form");
  const [phoneCode, setPhoneCode] = useState("");

  const phoneForm = useForm<z.infer<typeof phoneSchema>>({
    // @ts-ignore
    resolver: zodResolver(phoneSchema),
    defaultValues: {
      telefone: telefoneFromQuery || "",
    },
  });

  useEffect(() => {
    if (telefoneFromQuery) {
      setPhoneStep("code");
      const last = sessionStorage.getItem("sent_verification_phone");
      if (last === telefoneFromQuery) {
        // already requested recently in this session
        return;
      }
      sessionStorage.setItem("sent_verification_phone", telefoneFromQuery);
      handleRequestPhoneCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const phoneValue = phoneForm.watch("telefone") ?? telefoneFromQuery ?? "";
  const sanitizedPhone = useMemo(
    () => phoneValue.replace(/[^0-9]+/g, ""),
    [phoneValue]
  );

  const phoneDisplay = useMemo(() => {
    if (!sanitizedPhone) return "";
    return formatPhoneInput(sanitizedPhone);
  }, [sanitizedPhone]);

  const handleRequestPhoneCode = async () => {
    if (!sanitizedPhone || sanitizedPhone.length < 10) {
      showToast.error("Informe um telefone válido");
      return;
    }

    setIsLoading(true);
    try {
      await AuthService.requestVerificationCode({ telefone: sanitizedPhone });
      // mark as sent in sessionStorage to prevent duplicates across mounts
      sessionStorage.setItem("sent_verification_phone", sanitizedPhone);
      showToast.success("Código enviado! Verifique seu telefone.");
      setPhoneStep("code");
      setPhoneCode("");
    } catch (err) {
      showToast.error(
        err instanceof Error ? err.message : "Não foi possível enviar o código"
      );
      // clear session marker on failure so user can retry
      sessionStorage.removeItem("sent_verification_phone");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitPhoneForm = phoneForm.handleSubmit(async () => {
    await handleRequestPhoneCode();
  });

  const handleVerifyCode = async () => {
    if (phoneCode.length !== 6) {
      showToast.error("Informe o código completo");
      return;
    }

    setIsLoading(true);
    try {
      const response = await AuthService.verifyPhone({
        telefone: sanitizedPhone,
        codigo: phoneCode,
      });

      if (response && typeof response === "object") {
        if ("access" in response && "user" in response) {
          saveAuth(response as any);
          showToast.success("Telefone verificado com sucesso!");
          navigate({ to: "/app" });
          return;
        }

        if ((response as any).requiresEmailVerification || ((response as any).user && !(response as any).user.emailVerificado)) {
          // redirect to wait-verify-email with the user's email and telefone
          const userEmail = (response as any).user?.email || undefined;
          const userPhone = (response as any).user?.telefone || sanitizedPhone;
          // persist pending info in sessionStorage so verify-email callback can finalize login
          try {
            sessionStorage.setItem(
              "pending_verify_email",
              JSON.stringify({ email: userEmail, telefone: userPhone })
            );
          } catch (e) {}
          navigate({ to: "/auth/wait-verify-email", search: { email: userEmail, telefone: userPhone } });
          return;
        }

        if ((response as any).verified) {
          showToast.success("Telefone verificado! Redirecionando para login...");
          setTimeout(() => {
            navigate({ to: "/auth/identify" });
          }, 1500);
          return;
        }
      }

      // Fallback
      showToast.success("Telefone verificado! Redirecionando para login...");
      setTimeout(() => {
        navigate({ to: "/auth/identify" });
      }, 1500);
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : "Código inválido");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url("/authentication-bg.svg")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="w-full max-w-md relative z-10">
        <div className="bg-white rounded-4xl shadow-xl p-8">
          <div className="flex items-center justify-center mb-6">
            <Logo className="h-12" isTraditionalLogo />
          </div>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Verificar Telefone
            </h1>
            <p className="text-sm text-gray-600">
              Para continuar, precisamos confirmar seu número de telefone.
            </p>
          </div>

          {phoneStep === "form" ? (
            <Form {...phoneForm}>
              <form onSubmit={handleSubmitPhoneForm} className="space-y-4">
                <FormField
                  control={phoneForm.control}
                  name="telefone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefone</FormLabel>
                      <FormControl>
                        <Input
                          inputMode="tel"
                          placeholder="(00) 00000-0000"
                          value={field.value}
                          onChange={(event) =>
                            field.onChange(
                              formatPhoneInput(event.target.value)
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? "Enviando..." : "Receber código"}
                </Button>
              </form>
            </Form>
          ) : (
            <div className="space-y-5 text-center">
              <div className="space-y-1 text-sm text-gray-600">
                <p>Enviamos um código para {phoneDisplay}. Informe abaixo para verificar.</p>
              </div>

              <InputOTP
                maxLength={6}
                value={phoneCode}
                onChange={(value) => setPhoneCode(value)}
                containerClassName="justify-center"
              >
                <InputOTPGroup>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <InputOTPSlot key={index} index={index} />
                  ))}
                </InputOTPGroup>
              </InputOTP>

              <div className="space-y-2">
                <Button
                  type="button"
                  className="w-full"
                  disabled={phoneCode.length !== 6 || isLoading}
                  onClick={handleVerifyCode}
                >
                  {isLoading ? "Verificando..." : "Verificar código"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={isLoading}
                  onClick={() => {
                    setPhoneStep("form");
                    setPhoneCode("");
                  }}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Alterar telefone
                </Button>
              </div>
            </div>
          )}

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate({ to: "/auth/identify" })}
              className="text-sm text-gray-600 hover:text-blue-600 transition-colors"
            >
              Voltar para login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

